import { useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import { api, formatApiErrorDetail } from "../lib/api";
import { MailCheck } from "lucide-react";

/**
 * Forgot-password kickoff page (shared by clients + panelists — the backend
 * looks up the email across both user types).
 *
 * UX contract: the backend ALWAYS returns 200 to prevent user enumeration,
 * so we render the same confirmation regardless of whether the email
 * actually has an account on file. Never tell the user "no account found".
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Backend should always 200; any non-2xx is a transport issue.
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="forgot-password-page">
      <Navigation />
      <div className="max-w-md mx-auto px-6 py-16">
        {sent ? (
          <div data-testid="forgot-password-sent">
            <MailCheck className="w-12 h-12 text-[var(--rl-success)]" />
            <h1 className="font-display font-black text-3xl tracking-tight mt-5">Check your inbox.</h1>
            <p className="text-[var(--rl-text-secondary)] mt-4 leading-relaxed">
              If an account exists for <span className="font-semibold text-[var(--rl-text)]">{email}</span>, we just emailed you a secure link to reset your password. It expires in 1 hour.
            </p>
            <p className="text-xs text-[var(--rl-text-secondary)] mt-6">
              Didn't get anything? Check your spam folder, then{" "}
              <button onClick={() => { setSent(false); setEmail(""); }} className="text-[var(--rl-brand)] font-semibold underline" data-testid="forgot-password-try-again">try again</button>.
            </p>
            <Link to="/login" className="inline-block mt-8 text-sm font-semibold text-[var(--rl-brand)]" data-testid="forgot-password-back-to-login">← Back to sign in</Link>
          </div>
        ) : (
          <>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Account recovery</div>
            <h1 className="font-display font-black text-4xl tracking-tight mt-2">Forgot your password?</h1>
            <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">
              Enter the email tied to your Adrigo Insights AI account. We'll email you a single-use reset link.
            </p>
            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="forgot-password-form">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] font-bold mb-2">Email</label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)] transition-colors"
                  data-testid="forgot-password-email-input"
                />
              </div>
              {error && <div className="text-sm text-[var(--rl-accent)] font-medium" data-testid="forgot-password-error">{error}</div>}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[var(--rl-text)] text-white py-4 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50"
                data-testid="forgot-password-submit-btn"
              >
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </form>
            <Link to="/login" className="inline-block mt-6 text-sm font-semibold text-[var(--rl-brand)]" data-testid="forgot-password-back-to-login">← Back to sign in</Link>
          </>
        )}
      </div>
    </div>
  );
}
